import { useState, ReactNode } from 'react'

interface DrawerItem {
  id: string
  icon: ReactNode
  label: string
  content: ReactNode
  customHeader?: ReactNode
  isDivider?: never
  isDirectAction?: never
  onClick?: never
}

interface DirectActionItem {
  id: string
  icon: ReactNode
  label: string
  isDirectAction: true
  onClick: () => void
  content?: never
  customHeader?: never
  isDivider?: never
}

interface DividerItem {
  isDivider: true
  id?: never
  icon?: never
  label?: never
  content?: never
  customHeader?: never
}

type SidebarItem = DrawerItem | DirectActionItem | DividerItem

interface SidebarDrawerProps {
  items: SidebarItem[]
  className?: string
  activeDrawer?: string | null
  onDrawerChange?: (drawerId: string | null) => void
  mode?: 'editor' | 'preview' // Add mode prop to differentiate between editor and preview
  mobileMode?: boolean // Force mobile sidebar width regardless of screen size
  overlayMode?: boolean // New prop to enable overlay positioning mode
}

export function SidebarDrawer({ 
  items, 
  className = '', 
  activeDrawer: externalActiveDrawer, 
  onDrawerChange, 
  mode = 'editor',
  mobileMode = false,
  overlayMode = false
}: SidebarDrawerProps) {
  const [internalActiveDrawer, setInternalActiveDrawer] = useState<string | null>(null)
  
  // Use external state if provided, otherwise use internal state
  const activeDrawer = externalActiveDrawer !== undefined ? externalActiveDrawer : internalActiveDrawer
  const setActiveDrawer = onDrawerChange || setInternalActiveDrawer

  const handleItemClick = (item: DrawerItem | DirectActionItem) => {
    if (item.isDirectAction) {
      item.onClick()
    } else {
      setActiveDrawer(activeDrawer === item.id ? null : item.id)
    }
  }

  // Fixed drawer width for inline styles (desktop default)
  const drawerWidth = 270

  return (
    <div className={`${overlayMode ? '' : 'flex'} ${className}`}>
      {/* Collapsed Sidebar */}
      <div 
        className={`${
          overlayMode 
            ? 'fixed left-0 top-16 bottom-0 z-40 bg-gray-800 border-r border-gray-700' 
            : 'bg-gray-800 border-r border-gray-700'
        } flex flex-col shadow-sm w-12`}
        style={{ paddingLeft: 'env(safe-area-inset-left)' }}
      >
        <nav className="flex-1 p-2 space-y-1 pt-4 overflow-y-auto scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-600">
          {items.map((item, index) => {
            if (item.isDivider) {
              return (
                <div key={`divider-${index}`} className="h-px bg-gray-700 my-3 mx-2" />
              )
            }
            
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                className={`w-full p-2.5 rounded-lg transition-all duration-200 group relative ${
                  !item.isDirectAction && activeDrawer === item.id
                    ? 'bg-emerald-900/50 text-emerald-400 shadow-sm'
                    : 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                }`}
                title={item.label}
              >
                <div className="flex flex-col items-center">
                  <div className="mb-1 flex items-center justify-center [&>svg]:transition-all">
                    {item.icon}
                  </div>
                  <span className="font-medium text-center block text-[8px] leading-tight">
                    {item.label}
                  </span>
                </div>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Sliding Drawer */}
      <div 
        className={`${
          overlayMode 
            ? 'fixed left-12 top-16 bottom-0 z-40 bg-gray-800 border-r border-gray-700' 
            : 'bg-gray-800 border-r border-gray-700'
        } ${overlayMode ? 'flex flex-col' : ''} transition-all duration-300 ease-in-out ${
          activeDrawer ? (mobileMode ? 'w-[90px]' : 'w-[90px] sm:w-[180px] md:w-[270px]') : 'w-0'
        } ${!activeDrawer ? 'overflow-hidden' : ''}`}
        style={{ paddingLeft: overlayMode ? 'env(safe-area-inset-left)' : undefined }}
      >
        {activeDrawer && (
          <div className={`h-full flex flex-col relative z-10 ${mobileMode ? 'w-[90px]' : 'w-[90px] sm:w-[180px] md:w-[270px]'}`}>
            {/* Drawer Header - positioned at top */}
            <div className="px-4 py-2 border-b border-white/[0.06] bg-gradient-to-r from-emerald-900/20 to-teal-900/20 bg-gray-800 z-20">
              <div className="flex items-center gap-2">
                {(() => {
                  const activeItem = items.find(item => !item.isDivider && !item.isDirectAction && item.id === activeDrawer) as DrawerItem | undefined
                  return activeItem?.customHeader ? (
                    <div className="flex-1 min-w-0">
                      {activeItem.customHeader}
                    </div>
                  ) : (
                    <h3 className="text-xs font-semibold text-gray-100 flex-1">
                      {activeItem?.label}
                    </h3>
                  )
                })()}
                <button
                  onClick={() => setActiveDrawer(null)}
                  className="p-1 hover:bg-gray-700/60 rounded-lg transition-colors duration-200 flex-shrink-0 ml-2"
                >
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 relative" style={{ minHeight: 0 }}>
              <div 
                className="absolute inset-0 overflow-y-auto scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-600" 
                style={{ 
                  ...(overlayMode ? { paddingBottom: '600px' } : {})
                }}
              >
                {(items.find(item => !item.isDivider && !item.isDirectAction && item.id === activeDrawer) as DrawerItem | undefined)?.content}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}